import type { ModelInfo } from '@/components/viewer/ModelViewer'

export interface PrintabilityAnalysis {
  score: number // 0-100
  issues: PrintIssue[]
  recommendations: string[]
  estimatedPrintTime: number // seconds
  estimatedMaterial: number // grams (PLA at 1.24 g/cm³)
  difficulty: 'easy' | 'medium' | 'hard' | 'expert'
}

export interface PrintIssue {
  type: 'critical' | 'warning' | 'info'
  title: string
  description: string
}

const PLA_DENSITY = 1.24 // g/cm³
const PRINT_SPEED_MM_S = 50 // average print speed
const LAYER_HEIGHT = 0.2 // mm
const INFILL_PERCENT = 20

export function analyzeModelForPrinting(modelInfo: ModelInfo): PrintabilityAnalysis {
  const issues: PrintIssue[] = []
  const recommendations: string[] = []
  let score = 100

  // Check if model is manifold (watertight)
  if (!modelInfo.isManifold) {
    issues.push({
      type: 'critical',
      title: 'Non-Manifold Geometry',
      description: 'The model has holes or inverted normals. This may cause slicing issues.',
    })
    score -= 30
    recommendations.push('Use mesh repair tools to fix geometry issues')
  }

  // Check polygon count
  if (modelInfo.triangles > 1000000) {
    issues.push({
      type: 'warning',
      title: 'High Polygon Count',
      description: `${modelInfo.triangles.toLocaleString()} triangles may slow down slicing.`,
    })
    score -= 10
    recommendations.push('Consider decimating the mesh to reduce complexity')
  }

  // Check dimensions for printability
  const { width, height, depth } = modelInfo.boundingBox
  const maxDim = Math.max(width, height, depth)
  const minDim = Math.min(width, height, depth)

  if (maxDim > 300) {
    issues.push({
      type: 'warning',
      title: 'Large Model',
      description: 'Model may exceed typical printer build volume.',
    })
    score -= 15
    recommendations.push('Consider scaling down or splitting the model')
  }

  if (minDim < 1) {
    issues.push({
      type: 'warning',
      title: 'Very Thin Features',
      description: 'Some features may be too thin to print reliably.',
    })
    score -= 10
    recommendations.push('Increase wall thickness for better structural integrity')
  }

  // Check aspect ratio for stability
  if (height > width * 3 || height > depth * 3) {
    issues.push({
      type: 'info',
      title: 'Tall & Narrow',
      description: 'Model is tall relative to its base. May need supports.',
    })
    recommendations.push('Consider printing with a brim for better adhesion')
  }

  // Estimate print time (simplified calculation)
  const layerCount = height / LAYER_HEIGHT
  const perimeterLength = (width + depth) * 2
  const infillLength = (width * depth * INFILL_PERCENT / 100) / 0.4 // 0.4mm line width
  const totalLength = layerCount * (perimeterLength + infillLength)
  const estimatedPrintTime = Math.round((totalLength / PRINT_SPEED_MM_S) * 1.5) // 1.5x for moves

  // Estimate material usage
  const shellVolume = modelInfo.surfaceArea * 0.8 // ~0.8mm wall thickness
  const infillVolume = modelInfo.volume * (INFILL_PERCENT / 100)
  const totalVolume = shellVolume + infillVolume
  const estimatedMaterial = Math.round(totalVolume * PLA_DENSITY / 1000) // Convert to grams

  // Add positive recommendations
  if (modelInfo.isManifold && modelInfo.triangles < 500000) {
    recommendations.push('Model geometry looks good for printing')
  }

  if (score >= 90) {
    recommendations.push('This model should print well with standard settings')
  } else if (score >= 70) {
    recommendations.push('Review the issues above before printing')
  }

  // Determine difficulty
  let difficulty: PrintabilityAnalysis['difficulty']
  if (score >= 90 && maxDim < 150) {
    difficulty = 'easy'
  } else if (score >= 70) {
    difficulty = 'medium'
  } else if (score >= 50) {
    difficulty = 'hard'
  } else {
    difficulty = 'expert'
  }

  return {
    score: Math.max(0, score),
    issues,
    recommendations,
    estimatedPrintTime,
    estimatedMaterial,
    difficulty,
  }
}

export function formatPrintTime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`
  const hours = Math.floor(seconds / 3600)
  const mins = Math.round((seconds % 3600) / 60)
  return `${hours}h ${mins}m`
}

export function getDifficultyColor(difficulty: PrintabilityAnalysis['difficulty']): string {
  switch (difficulty) {
    case 'easy': return 'text-success-400'
    case 'medium': return 'text-warning-400'
    case 'hard': return 'text-orange-400'
    case 'expert': return 'text-red-400'
  }
}

export function getScoreColor(score: number): string {
  if (score >= 80) return 'text-success-400'
  if (score >= 60) return 'text-warning-400'
  if (score >= 40) return 'text-orange-400'
  return 'text-red-400'
}
